import { authService } from "../authService/authService";
import { GetRefreshToken, SaveTokens, ClearTokens, IsTokenNearExpiration, IsAuthorized } from "../../utils/helpers/auth";
import { routeUrls } from "../../config/routeUrls";
import settings from "../../settings";

class TokenManager {
    private refreshPromise: Promise<boolean> | null = null;
    private refreshInterval: NodeJS.Timeout | null = null;
    private isInitialized: boolean = false;
    private retryCount: number = 0;
    private maxRetries: number = 2; // Ridotto da 3 a 2 per token da 5 minuti

    // Inizializza il token manager in modo completamente silente
    public initialize() {
        if (this.isInitialized) {
            return;
        }

        this.isInitialized = true;
        this.startSilentTokenManagement();
        
        console.log('TokenManager initialized - silent refresh enabled');
    }

    // Ferma completamente il token manager
    public shutdown() {
        this.stopTokenCheck();
        this.isInitialized = false;
        this.refreshPromise = null;
        console.log('TokenManager shutdown');
    }

    // Avvia la gestione completamente silente del token
    private startSilentTokenManagement() {
        this.stopTokenCheck();
        
        // Controlla immediatamente se serve un refresh
        this.performSilentCheck();
        
        // Per token da 5 minuti: controllo ogni 15 secondi per essere più reattivo
        this.refreshInterval = setInterval(() => {
            this.performSilentCheck();
        }, 15000); // 15 secondi invece di 30
    }

    // Esegue un controllo silente senza disturbare l'utente
    private async performSilentCheck() {
        // Non fare nulla se l'utente non è autorizzato
        if (!IsAuthorized()) {
            return;
        }

        // Refresh 2.5 minuti prima della scadenza
        if (IsTokenNearExpiration(2.5)) {
            console.log('Token near expiration (2.5 min threshold), attempting silent refresh...');
            await this.refreshTokenSilently();
        }
    }

    // Ferma il controllo periodico
    public stopTokenCheck() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    // Refresha il token in modo completamente silente
    public async refreshTokenSilently(): Promise<boolean> {
        // Se è già in corso un refresh, attendi quello
        if (this.refreshPromise) {
            console.log('🔄 Refresh already in progress, waiting...');
            return this.refreshPromise;
        }

        const refreshToken = GetRefreshToken();
        console.log('🔍 TokenManager: Attempting refresh, refresh token available:', !!refreshToken);
        
        if (!refreshToken) {
            console.log('❌ No refresh token available, silent refresh not possible');
            return false;
        }

        console.log('🚀 Starting silent refresh process...');
        this.refreshPromise = this.performSilentRefresh(refreshToken);
        const result = await this.refreshPromise;
        this.refreshPromise = null;
        
        console.log('✅ Silent refresh process completed:', result ? 'SUCCESS' : 'FAILED');
        return result;
    }

    private async performSilentRefresh(refreshToken: string): Promise<boolean> {
        return new Promise((resolve) => {
            try {
                authService.refreshToken(
                    refreshToken, 
                    settings.clientId,
                    // Success callback
                    (data: any) => {
                        console.log('✅ RefreshToken success callback received:', data);
                        if (data.success) {
                            const tokenData = JSON.parse(data.responseBody.token);
                            
                            // Salva tutti i token, incluso l'idToken se presente
                            SaveTokens(
                                tokenData.access_token,
                                tokenData.refresh_token,
                                tokenData.expires_in
                            );
                            
                            // Aggiorna anche l'idToken se presente
                            if (tokenData.id_token) {
                                window.sessionStorage.setItem('idToken', tokenData.id_token);
                            }
                            
                            console.log("✅ Silent token refresh successful");
                            this.retryCount = 0; // Reset retry counter on success
                            resolve(true);
                        } else {
                            console.warn("❌ Silent token refresh failed:", data);
                            resolve(this.handleRefreshFailure());
                        }
                    },
                    // Error callback
                    () => {
                        console.error("❌ Silent token refresh error callback");
                        resolve(this.handleRefreshFailure());
                    }
                );
            } catch (error) {
                console.error("❌ Silent token refresh exception:", error);
                resolve(this.handleRefreshFailure());
            }
        });
    }

    private handleRefreshFailure(): boolean {
        this.retryCount++;
        
        // Se abbiamo superato il numero massimo di retry
        if (this.retryCount >= this.maxRetries) {
            console.log(`❌ Max retries (${this.maxRetries}) reached for 5-min token, clearing tokens and redirecting...`);
            this.clearTokensAndRedirect();
            return false;
        }
        
        // Retry più veloce per token da 5 minuti: 5 secondi invece di 10
        console.log(`🔄 Retry ${this.retryCount}/${this.maxRetries} - will retry in 5 seconds (5-min token optimization)...`);
        
        setTimeout(() => {
            if (IsAuthorized()) {
                this.performSilentCheck();
            }
        }, 5000); // 5 secondi invece di 10
        
        return false;
    }

    private clearTokensAndRedirect() {
        ClearTokens();
        this.stopTokenCheck();
        
        // Solo se siamo in una pagina protetta, reindirizza al login
        const protectedPaths = ['/dashboard', '/services', '/documents', '/contracts', '/fleet-and-route', '/settings', '/profile'];
        const currentPath = window.location.pathname;
        
        if (protectedPaths.some(path => currentPath.startsWith(path))) {
            console.log('Redirecting to authorize due to token refresh failure');
            window.location.href = routeUrls.authorize; // Usa authorize invece di logout per una transizione più pulita
        }
    }

    // Ottieni lo stato del token manager
    public getStatus() {
        return {
            isInitialized: this.isInitialized,
            isRefreshInProgress: this.refreshPromise !== null,
            retryCount: this.retryCount,
            maxRetries: this.maxRetries
        };
    }
}

export const tokenManager = new TokenManager();